import * as THREE from 'three';
import { clamp } from './util.js';

/* Lifted out of the single-file build unchanged, minus buildTextGeometry —
   that one rasterises glyphs through a canvas and assembles THREE geometry, so
   it sits with the other geometry builders in scene/geometry.js. What is left
   here is the tracing itself, which needs nothing but an alpha buffer. */

/* ═══════════════════════════════════════════════════════════
   GLYPH OUTLINES  →  REAL EXTRUDED GEOMETRY

   Stacking alpha-tested planes gives no side walls: every slice faces +Z, so
   the "extrusion" is just a silhouette edge that shades identically whichever
   way it points, with visible striations between slices.

   Instead: render the glyphs, march squares over the alpha channel at the 0.5
   iso-contour, stitch the segments into closed loops, work out which loops are
   holes, and feed real Shapes to ExtrudeGeometry. That yields true side walls
   with correct normals, a bevel that catches a highlight, a clean geometric
   silhouette, and proper shadow casting — while still working with any typeface
   installed on the machine, since nothing here needs a font binary.
   ═══════════════════════════════════════════════════════════ */

/* Marching squares at threshold `t`. `alpha` is w*h in 0..1, row-major.
   Returns closed loops as arrays of [x,y] in pixel space. */
export function traceIso(alpha, w, h, t){
  const W = w+2, H = h+2;                 // pad so shapes touching the edge close
  const g = new Float32Array(W*H);
  for(let y=0;y<h;y++){
    const src = y*w, dst = (y+1)*W + 1;
    for(let x=0;x<w;x++) g[dst+x] = alpha[src+x];
  }
  const segs = [];
  const cut = (ax,ay,va,bx,by,vb)=>{
    const d = vb-va;
    const k = Math.abs(d) < 1e-12 ? .5 : (t-va)/d;
    return [ax+(bx-ax)*k, ay+(by-ay)*k];
  };
  for(let y=0;y<H-1;y++){
    for(let x=0;x<W-1;x++){
      const i = y*W+x;
      const va=g[i], vb=g[i+1], vc=g[i+1+W], vd=g[i+W];
      const idx = (va>t?1:0)|(vb>t?2:0)|(vc>t?4:0)|(vd>t?8:0);
      if(idx===0 || idx===15) continue;
      const X=x-1, Y=y-1;                 // undo the pad
      const top    = ()=>cut(X,Y,va, X+1,Y,vb);
      const right  = ()=>cut(X+1,Y,vb, X+1,Y+1,vc);
      const bottom = ()=>cut(X,Y+1,vd, X+1,Y+1,vc);
      const left   = ()=>cut(X,Y,va, X,Y+1,vd);
      const E = {top, right, bottom, left};
      let pairs;
      switch(idx){
        case 1: case 14: pairs=[['top','left']]; break;
        case 2: case 13: pairs=[['top','right']]; break;
        case 3: case 12: pairs=[['left','right']]; break;
        case 4: case 11: pairs=[['right','bottom']]; break;
        case 6: case 9:  pairs=[['top','bottom']]; break;
        case 7: case 8:  pairs=[['left','bottom']]; break;
        // Saddles: the cell centre decides whether the two inside corners join.
        case 5:  pairs = ((va+vb+vc+vd)/4 > t)
                   ? [['top','right'],['left','bottom']]
                   : [['top','left'],['right','bottom']]; break;
        case 10: pairs = ((va+vb+vc+vd)/4 > t)
                   ? [['top','left'],['right','bottom']]
                   : [['top','right'],['left','bottom']]; break;
        default: pairs=[];
      }
      for(const [p,q] of pairs) segs.push([E[p](), E[q]()]);
    }
  }
  return stitchLoops(segs);
}

/* Segments meeting on a shared cell edge are computed from the same two corner
   values, so their endpoints match exactly and can be keyed directly. */
export function stitchLoops(segs){
  const K = p => (Math.round(p[0]*4096)/4096)+','+(Math.round(p[1]*4096)/4096);
  const at = new Map();
  segs.forEach((sg,i)=>{
    for(const p of sg){
      const k = K(p);
      let L = at.get(k);
      if(!L){ L=[]; at.set(k,L); }
      L.push(i);
    }
  });
  const used = new Uint8Array(segs.length);
  const loops = [];
  for(let i=0;i<segs.length;i++){
    if(used[i]) continue;
    used[i]=1;
    const loop = [segs[i][0], segs[i][1]];
    let end = segs[i][1];
    const startK = K(loop[0]);
    for(let guard=0; guard<segs.length+4; guard++){
      const cands = at.get(K(end));
      let nx = -1;
      if(cands) for(const j of cands){ if(!used[j]){ nx=j; break; } }
      if(nx < 0) break;
      used[nx]=1;
      const sg = segs[nx];
      const other = (K(sg[0])===K(end)) ? sg[1] : sg[0];
      loop.push(other);
      end = other;
      if(K(end)===startK) break;
    }
    if(loop.length >= 4) loops.push(loop);
  }
  return loops;
}

/* Douglas–Peucker on an open polyline. */
export function simplifyOpen(pts, tol){
  const n = pts.length;
  if(n < 3) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = keep[n-1] = 1;
  const stack = [[0, n-1]];
  while(stack.length){
    const [a,b] = stack.pop();
    if(b - a < 2) continue;
    const p = pts[a], q = pts[b];
    const dx = q[0]-p[0], dy = q[1]-p[1];
    const len = Math.hypot(dx,dy) || 1;
    let worst = -1, at = -1;
    for(let i=a+1;i<b;i++){
      const d = Math.abs((pts[i][0]-p[0])*dy - (pts[i][1]-p[1])*dx)/len;
      if(d > worst){ worst = d; at = i; }
    }
    if(worst > tol){ keep[at]=1; stack.push([a,at],[at,b]); }
  }
  const out = [];
  for(let i=0;i<n;i++) if(keep[i]) out.push(pts[i]);
  return out;
}

/* Marching squares emits a vertex per cell crossing, so an 'O' arrives with a
   few thousand points.

   Douglas–Peucker cannot be pointed straight at a closed ring: the first and
   last vertex coincide, so the baseline has zero length and every perpendicular
   distance evaluates to zero — which silently flattens every contour to two
   points. Split the ring at its most distant vertex and simplify each half.
   Returns an open ring (no duplicated closing vertex). */
export function simplifyLoop(pts, tol){
  let p = pts;
  const last = p.length-1;
  if(p.length > 2 && Math.abs(p[0][0]-p[last][0]) < 1e-9 && Math.abs(p[0][1]-p[last][1]) < 1e-9)
    p = p.slice(0, -1);
  const n = p.length;
  if(n < 5) return p;
  let far = 0, fd = -1;
  for(let i=1;i<n;i++){
    const dx = p[i][0]-p[0][0], dy = p[i][1]-p[0][1];
    const d = dx*dx + dy*dy;
    if(d > fd){ fd = d; far = i; }
  }
  if(far < 2) return p;
  const A = simplifyOpen(p.slice(0, far+1), tol);          // p[0] … p[far]
  const B = simplifyOpen(p.slice(far).concat([p[0]]), tol); // p[far] … p[0]
  return A.concat(B.slice(1, -1));
}

export const loopArea = pts=>{
  let a = 0;
  for(let i=0,j=pts.length-1;i<pts.length;j=i++) a += pts[j][0]*pts[i][1] - pts[i][0]*pts[j][1];
  return a/2;
};
export const pointInLoop = (pt, poly)=>{
  let inside = false;
  for(let i=0,j=poly.length-1;i<poly.length;j=i++){
    const xi=poly[i][0], yi=poly[i][1], xj=poly[j][0], yj=poly[j][1];
    if(((yi>pt[1]) !== (yj>pt[1])) && (pt[0] < (xj-xi)*(pt[1]-yi)/((yj-yi)||1e-12)+xi)) inside = !inside;
  }
  return inside;
};

/* Nest loops into Shapes. Even containment depth is an outline, odd is a hole
   (the counter of an 'O'), and each hole belongs to the tightest outline
   around it — which is what keeps 'O' inside 'Q' style glyphs correct. */
export function loopsToShapes(loops){
  const meta = loops.map(l=>({pts:l, area:Math.abs(loopArea(l)), depth:0, parent:null}));
  for(let i=0;i<meta.length;i++)
    for(let j=0;j<meta.length;j++){
      if(i===j) continue;
      if(pointInLoop(meta[i].pts[0], meta[j].pts)) meta[i].depth++;
    }
  const outers = meta.filter(m=>m.depth%2===0);
  const inners = meta.filter(m=>m.depth%2===1);
  for(const h of inners){
    let best = null;
    for(const out of outers)
      if(pointInLoop(h.pts[0], out.pts) && (!best || out.area < best.area)) best = out;
    h.parent = best;
  }
  const toV2 = p=>new THREE.Vector2(p[0], p[1]);
  const shapes = [];
  for(const out of outers){
    const pts = loopArea(out.pts) < 0 ? out.pts.slice().reverse() : out.pts;   // outline CCW
    const sh = new THREE.Shape(pts.map(toV2));
    for(const h of inners){
      if(h.parent !== out) continue;
      const hp = loopArea(h.pts) > 0 ? h.pts.slice().reverse() : h.pts;        // hole CW
      sh.holes.push(new THREE.Path(hp.map(toV2)));
    }
    shapes.push(sh);
  }
  return shapes;
}

/* Angle-based normal smoothing — what Blender calls Auto Smooth and 3ds Max
   calls smoothing groups.

   ExtrudeGeometry hands back a non-indexed mesh, so computeVertexNormals gives
   every triangle its own flat normal. Across the curved side of an O that reads
   as hard diagonal banding. Weld vertices by position, then average each
   vertex's normal across only those neighbouring faces whose crease angle is
   shallower than the threshold. Curves go smooth; the sharp corners of letter
   stems stay crisp, because those creases are far too steep to qualify.

   Flat faces must come out untouched, and a crease-angle test alone does not
   guarantee that. ExtrudeGeometry triangulates each cap with no interior
   vertices, so EVERY cap vertex sits on the outline, welded to the bevel ring
   beside it. With a multi-segment bevel that junction is a shallow angle, so the
   whole flat face gets dragged outward and an O shades like a tube.

   The structural boundary is what matters, not the angle: ExtrudeGeometry emits
   group 0 for the caps and group 1 for the sides. Averaging is confined within a
   group, so a cap can only ever average other cap faces — all identical — and
   stays exactly planar no matter how the crease angle is set. */
export function creaseNormals(geo, angleDeg){
  const pos = geo.attributes.position;
  const n = pos.count;
  if(!n || n%3) return geo;
  const cosT = Math.cos(clamp(angleDeg, 0, 179)*Math.PI/180);
  const faces = n/3;

  const fn = new Float32Array(faces*3);
  const solid = new Uint8Array(faces);
  const area = new Float32Array(faces);
  const a=new THREE.Vector3(), b=new THREE.Vector3(), c=new THREE.Vector3();
  const e1=new THREE.Vector3(), e2=new THREE.Vector3(), nr=new THREE.Vector3();
  for(let f=0;f<faces;f++){
    a.fromBufferAttribute(pos, f*3);
    b.fromBufferAttribute(pos, f*3+1);
    c.fromBufferAttribute(pos, f*3+2);
    e1.subVectors(b,a); e2.subVectors(c,a);
    nr.crossVectors(e1,e2);
    /* Slivers have no meaningful direction, and a simplified glyph contour will
       occasionally produce one. Flag them so they neither receive a fabricated
       normal nor drag their neighbours off — the earlier version handed them a
       (0,0,1) fallback, which showed up as a 90 degree seam on curved walls. */
    const lim = Math.max(e1.lengthSq(), e2.lengthSq()) * 1e-7;
    const cross = nr.length();
    if(cross*cross <= lim){ solid[f]=0; continue; }
    area[f] = cross*.5;
    nr.divideScalar(cross);
    solid[f]=1;
    fn[f*3]=nr.x; fn[f*3+1]=nr.y; fn[f*3+2]=nr.z;
  }

  // weld by quantised position (0.01 mm buckets)
  const Q = 1e5;
  const buckets = new Map();
  const keys = new Array(n);
  for(let i=0;i<n;i++){
    const k = Math.round(pos.getX(i)*Q)+'|'+Math.round(pos.getY(i)*Q)+'|'+Math.round(pos.getZ(i)*Q);
    keys[i] = k;
    let L = buckets.get(k);
    if(!L){ L=[]; buckets.set(k,L); }
    L.push(i);
  }

  /* Which structural group each face belongs to. ExtrudeGeometry gives caps and
     sides separate groups; anything without groups is treated as one surface. */
  const groupOf = new Int32Array(faces);
  if(geo.groups && geo.groups.length > 1){
    geo.groups.forEach((grp, gi)=>{
      const f0 = Math.floor(grp.start/3);
      const f1 = Math.min(faces, Math.floor((grp.start + grp.count)/3));
      for(let f=f0; f<f1; f++) groupOf[f] = gi;
    });
  }

  const out = new Float32Array(n*3);
  for(let i=0;i<n;i++){
    const f = (i/3)|0;
    const nx=fn[f*3], ny=fn[f*3+1], nz=fn[f*3+2];
    let sx=0, sy=0, sz=0;
    const share = buckets.get(keys[i]);
    if(share.length > 96){                           // pathological weld, keep it flat
      sx=nx; sy=ny; sz=nz;
    }else if(!solid[f]){
      /* A sliver renders no fragments, so its direction is arbitrary — but it must
         not average the wall with a cap and end up tilted, which is what produced
         a visible seam. Inherit the largest real face welded at this point. */
      let bestA = -1, bi = -1;
      for(const j of share){
        const g = (j/3)|0;
        if(solid[g] && groupOf[g]===groupOf[f] && area[g] > bestA){ bestA = area[g]; bi = g; }
      }
      if(bi >= 0){ sx=fn[bi*3]; sy=fn[bi*3+1]; sz=fn[bi*3+2]; }
    }else for(const j of share){
      const g = (j/3)|0;
      if(!solid[g]) continue;                        // slivers never contribute
      if(groupOf[g] !== groupOf[f]) continue;        // a cap never averages a side
      const gx=fn[g*3], gy=fn[g*3+1], gz=fn[g*3+2];
      if(nx*gx+ny*gy+nz*gz >= cosT){ sx+=gx; sy+=gy; sz+=gz; }
    }
    const len = Math.hypot(sx,sy,sz);
    if(len < 1e-9){ sx=nx; sy=ny; sz=nz; }
    else { sx/=len; sy/=len; sz/=len; }
    // Within half a degree of the face normal means the patch is flat: snap back
    // so a planar surface stays bit-exact rather than drifting on float error.
    if(sx*nx + sy*ny + sz*nz > 0.9999619){ sx=nx; sy=ny; sz=nz; }
    out[i*3]=sx; out[i*3+1]=sy; out[i*3+2]=sz;
  }
  geo.setAttribute('normal', new THREE.BufferAttribute(out,3));
  geo.attributes.normal.needsUpdate = true;
  return geo;
}

export const TEXT_DETAIL = {
  coarse:{cap:150, tol:1.5},
  normal:{cap:250, tol:0.75},
  fine:  {cap:420, tol:0.34}
};
